import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SearchBar, Select, type Column, Icon } from "@legal-500/legal500-design-system";
import { DataTable } from "../../components/DataTable/DataTable";
import { DetailModal } from "../../components/DetailModal/DetailModal";
import { PaginationControls } from "../../components/PaginationControls";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { rankings, type Ranking } from "../../api/rankings.service";
import {
  networkJurisdictionRankings,
  type NetworkJurisdictionRanking,
} from "../../api/networkJurisdictionRankings.service";
import type { CompanyUser } from "../../api/companyUsers.service";
import { useCompanyUsers } from "../../hooks/useCompanyUsers";
import { COLUMN_CONFIGS, MODAL_FIELD_CONFIGS } from "../CompanyUsers/companyUsersConfig";
import {
  COLUMN_CONFIGS as RANKINGS_COLUMN_CONFIGS,
  MODAL_FIELD_CONFIGS as RANKINGS_MODAL_FIELD_CONFIGS,
} from "./rankingsConfig";
import { companySummaries, type CompanySummaryListItem } from "../../api/companySummaries.service";

import {
  intelligence,
  type IntelligenceArticleFeed,
  type IntelligenceArticleDetail,
  type IntelligenceTopic,
  type IntelligenceRegion,
  type IntelligenceCountry,
  type IntelligenceEvent,
  type IntelligenceGuide,
  type IntelligenceArticleAuthor,
  type IntelligenceArticleTopic,
  type IntelligenceArticleDisclaimer,
  type IntelligenceArticleState,
  type IntelligenceState,
} from "../../api/intelligence.service";
import styles from "./DataMartDetail.module.css";

type TableConfig = {
  id: string;
  name: string;
  description: string;
};

type DataMartConfig = {
  id: string;
  name: string;
  description: string;
  tables: TableConfig[];
};

const DATA_MARTS: Record<string, DataMartConfig> = {
  account: {
    id: "account",
    name: "Account",
    description: "User accounts and permissions associated with law firm companies",
    tables: [
      {
        id: "company-users",
        name: "Company Users",
        description:
          "User accounts associated with law firm companies, including permissions and contact details",
      },
    ],
  },
  rankings: {
    id: "rankings",
    name: "Rankings",
    description: "Firm rankings by practice area, region, and tier from Legal 500 editorial data",
    tables: [
      {
        id: "fct-rankings",
        name: "Rankings",
        description:
          "Denormalized ranking records with firm, practice area, region, and accolade data",
      },
    ],
  },
  "network-jurisdiction-rankings": {
    id: "network-jurisdiction-rankings",
    name: "Network Jurisdiction Rankings",
    description: "Jurisdiction-level scores for law firm networks across countries and editions",
    tables: [
      {
        id: "network-jurisdiction-ranking",
        name: "Network Jurisdiction Rankings",
        description:
          "Denormalized ranking records with network, firm, country, edition, and score data",
      },
    ],
  },
  "company-summaries": {
    id: "company-summaries",
    name: "Company Summaries",
    description:
      "Pre-built firm comparison summaries with rankings, ratings, testimonials, and offices",
    tables: [
      {
        id: "company-summary",
        name: "Company Summaries",
        description: "Denormalised summaries keyed by company, practice area, country, and site",
      },
    ],
  },
  intelligence: {
    id: "intelligence",
    name: "Intelligence",
    description:
      "Mondaq intelligence articles, topics, regions, events, and guides synced from the Snowflake mart",
    tables: [
      {
        id: "article-feed",
        name: "Article Feed",
        description: "Denormalized article feed records keyed by article, region, and topic",
      },
      {
        id: "article-detail",
        name: "Article Detail",
        description:
          "Full article detail including content, company, topic, country, and publish status",
      },
      {
        id: "topics",
        name: "Topics",
        description: "Intelligence topic dimension — topic names and slugs",
      },
      {
        id: "regions",
        name: "Regions",
        description: "Intelligence region dimension with parent-child hierarchy",
      },
      {
        id: "countries",
        name: "Countries",
        description: "Intelligence country dimension linked to regions",
      },
      {
        id: "events",
        name: "Events",
        description: "Intelligence events with topic, region, country, and date data",
      },
      {
        id: "guides",
        name: "Guides",
        description: "Intelligence comparative guides linked to topics",
      },
      {
        id: "article-authors",
        name: "Article Authors",
        description:
          "Authors associated with intelligence articles, including profile and sort data",
      },
      {
        id: "article-topics",
        name: "Article Topics",
        description: "Topic associations for intelligence articles with parent/primary flags",
      },
      {
        id: "article-disclaimers",
        name: "Article Disclaimers",
        description: "Disclaimer text records linked to intelligence articles by company rules",
      },
      {
        id: "article-states",
        name: "Article States",
        description: "US state associations for intelligence articles with country context",
      },
      {
        id: "states",
        name: "States",
        description: "State dimension table with country associations and state codes",
      },
    ],
  },
};

function RankingsTable() {
  const [rankingsData, setRankingsData] = useState<Ranking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedRanking, setSelectedRanking] = useState<Ranking | null>(null);

  const fetchRankings = useCallback(async (page: number, pageSize: number, searchTerm: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await rankings.getAll({
        search: searchTerm || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      setRankingsData(response.rankings);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load rankings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings(currentPage, itemsPerPage, search);
  }, [fetchRankings, currentPage, itemsPerPage, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const columns: Column<Ranking>[] = RANKINGS_COLUMN_CONFIGS.map(config => ({
    header: config.header,
    accessor: config.accessor,
    render: (value: unknown) => {
      if (value === null || value === undefined) return "-";
      if (typeof value === "boolean") return value ? "Yes" : "No";
      return String(value);
    },
  }));

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load rankings"
        message={error}
        action={{
          label: "Retry",
          onClick: () => fetchRankings(currentPage, itemsPerPage, search),
        }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder="Search rankings by company name..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading rankings..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={rankingsData}
              columns={columns}
              rowKey="id"
              emptyMessage="No rankings found."
              onRowClick={setSelectedRanking}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="rankings"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selectedRanking}
        onClose={() => setSelectedRanking(null)}
        item={selectedRanking}
        title={selectedRanking?.companyName || "Ranking Details"}
        fields={RANKINGS_MODAL_FIELD_CONFIGS}
        renderValue={(ranking: Ranking, key: keyof Ranking) => {
          const value = ranking[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "boolean") return value ? "Yes" : "No";
          return String(value);
        }}
      />
    </div>
  );
}

function NetworkJurisdictionRankingsTable() {
  const [njRankings, setNjRankings] = useState<NetworkJurisdictionRanking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchRankings = useCallback(async (page: number, pageSize: number, searchTerm: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await networkJurisdictionRankings.getAll({
        search: searchTerm || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });

      setNjRankings(response.data);
      setTotal(response.meta.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load rankings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRankings(currentPage, itemsPerPage, search);
  }, [fetchRankings, currentPage, itemsPerPage, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const columns: Column<NetworkJurisdictionRanking>[] = useMemo(
    () => [
      {
        header: "Network Name",
        accessor: "networkName" as keyof NetworkJurisdictionRanking,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Firm Name",
        accessor: "firmName" as keyof NetworkJurisdictionRanking,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Country",
        accessor: "countryName" as keyof NetworkJurisdictionRanking,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Edition",
        accessor: "edition" as keyof NetworkJurisdictionRanking,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Score",
        accessor: "score" as keyof NetworkJurisdictionRanking,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load rankings"
        message={error}
        action={{
          label: "Retry",
          onClick: () => fetchRankings(currentPage, itemsPerPage, search),
        }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder="Search rankings by network or firm name..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading rankings..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={njRankings}
              columns={columns}
              rowKey="id"
              emptyMessage="No rankings found."
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="rankings"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CompanyUsersTable() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [selectedUser, setSelectedUser] = useState<CompanyUser | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading, error } = useCompanyUsers({
    search: debouncedSearch || undefined,
    limit: itemsPerPage,
    offset: (currentPage - 1) * itemsPerPage,
  });

  const users = data?.data || [];
  const meta = data?.meta || { total: 0, limit: itemsPerPage, offset: 0 };
  const totalPages = Math.ceil(meta.total / itemsPerPage);
  const startIndex = meta.offset + 1;
  const endIndex = Math.min(meta.offset + users.length, meta.total);

  const columns: Column<CompanyUser>[] = COLUMN_CONFIGS.map(config => ({
    header: config.header,
    accessor: config.accessor,
    render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
  }));

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load users"
        message={error instanceof Error ? error.message : "Internal server error"}
        action={{
          label: "Retry",
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={value => {
              setSearch(value);
            }}
            onClear={() => {
              setSearch("");
              setDebouncedSearch("");
              setCurrentPage(1);
            }}
            placeholder="Search by name, email, company, or firm reference..."
          />
        </div>
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="companyUsersPerPage" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <Select
            id="companyUsersPerPage"
            value={itemsPerPage}
            onChange={e => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            options={[
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            size="small"
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading users..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={users}
              columns={columns}
              rowKey="userId"
              emptyMessage="No users found."
              onRowClick={setSelectedUser}
            />
          </div>

          {meta.total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={meta.total}
                itemName="users"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        item={selectedUser}
        title={selectedUser?.userName || "User Details"}
        fields={MODAL_FIELD_CONFIGS}
        renderValue={(user: CompanyUser, key: keyof CompanyUser) => {
          if (key === "metadata") {
            return user.metadata ? (
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {JSON.stringify(user.metadata, null, 2)}
              </pre>
            ) : (
              "No metadata"
            );
          }
          return String(user[key] ?? "N/A");
        }}
      />
    </div>
  );
}

function truncateBodyText(text: string | null, maxLen = 200): string {
  if (!text) return "N/A";
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
}

function IntelligenceArticleFeedTable() {
  const [items, setItems] = useState<IntelligenceArticleFeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleFeed | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleFeed.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article feed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const columns: Column<IntelligenceArticleFeed>[] = useMemo(
    () => [
      {
        header: "Title",
        accessor: "articleTitle" as keyof IntelligenceArticleFeed,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Topic",
        accessor: "topicName" as keyof IntelligenceArticleFeed,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Company",
        accessor: "companyName" as keyof IntelligenceArticleFeed,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Content Type",
        accessor: "intelligenceContentType" as keyof IntelligenceArticleFeed,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Published",
        accessor: "publishedTs" as keyof IntelligenceArticleFeed,
        render: (value: unknown) => (value ? new Date(value as string).toLocaleDateString() : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article feed"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder="Search article feed by title..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading article feed..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No articles found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="articles"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.articleTitle || "Article Feed Details"}
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "articleId", label: "Article ID" },
          { key: "intelligenceRegionId", label: "Region ID" },
          { key: "intelligenceCountryId", label: "Country ID" },
          { key: "intelligenceContentType", label: "Content Type" },
          { key: "articleSlug", label: "Slug" },
          { key: "articleTitle", label: "Title" },
          { key: "articleDescription", label: "Description" },
          { key: "articleSummary", label: "Summary" },
          { key: "locationName", label: "Location" },
          { key: "publishedTs", label: "Published" },
          { key: "intelligenceTopicId", label: "Topic ID" },
          { key: "topicName", label: "Topic" },
          { key: "companyId", label: "Company ID" },
          { key: "companyName", label: "Company" },
          { key: "numHighlightReads", label: "Highlight Reads" },
          { key: "numMustReads", label: "Must Reads" },
          { key: "numPopularReads", label: "Popular Reads" },
          { key: "isHighlight", label: "Is Highlight" },
          { key: "isMustRead", label: "Is Must Read" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceArticleFeed, key: keyof IntelligenceArticleFeed) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "boolean") return value ? "Yes" : "No";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceArticleDetailTable() {
  const [items, setItems] = useState<IntelligenceArticleDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleDetail | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number, searchTerm: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleDetail.getAll({
        search: searchTerm || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article detail");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage, search);
  }, [fetchData, currentPage, itemsPerPage, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const columns: Column<IntelligenceArticleDetail>[] = useMemo(
    () => [
      {
        header: "Article ID",
        accessor: "articleId" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Article Slug",
        accessor: "articleSlug" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => {
          // Slugs are country/topic/title paths that run well over 100 chars and
          // would push every following column off-screen. Full value is in the modal.
          const slug = value as string | null;
          if (!slug) return "-";
          return slug.length > 60 ? `${slug.slice(0, 60)}...` : slug;
        },
      },
      {
        header: "Title",
        accessor: "articleTitle" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Primary Topic",
        accessor: "primaryTopicName" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Company",
        accessor: "companyName" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Publish Status",
        accessor: "publishStatusName" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Published",
        accessor: "publishedTs" as keyof IntelligenceArticleDetail,
        render: (value: unknown) => (value ? new Date(value as string).toLocaleDateString() : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article detail"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage, search) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder="Search article detail by title, slug, or ID..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading article detail..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="articleId"
              emptyMessage="No articles found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="articles"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.articleTitle || "Article Detail"}
        fields={[
          { key: "articleId", label: "Article ID" },
          { key: "articleSlug", label: "Slug" },
          { key: "articleTitle", label: "Title" },
          { key: "articleDescription", label: "Description" },
          { key: "articleSummary", label: "Summary" },
          { key: "intelligenceContentType", label: "Content Type" },
          { key: "intelligenceCountryId", label: "Country ID" },
          { key: "countryIsoAlpha2", label: "Country ISO" },
          { key: "locationName", label: "Location" },
          { key: "sourceUrl", label: "Source URL" },
          { key: "publishedTs", label: "Published" },
          { key: "lastModifiedTs", label: "Last Modified" },
          { key: "primaryTopicId", label: "Primary Topic ID" },
          { key: "primaryTopicName", label: "Primary Topic" },
          { key: "publishStatusName", label: "Publish Status" },
          { key: "isPrimaryAuthorHidden", label: "Primary Author Hidden" },
          { key: "companyId", label: "Company ID" },
          { key: "companyName", label: "Company" },
          { key: "companyProfileSlug", label: "Company Slug" },
          { key: "companyIsSubscriber", label: "Company Subscriber" },
          { key: "isAwardWinner", label: "Award Winner" },
          { key: "rowSyncedTs", label: "Row Synced" },
          { key: "bodyText", label: "Body Text" },
        ]}
        renderValue={(item: IntelligenceArticleDetail, key: keyof IntelligenceArticleDetail) => {
          if (key === "bodyText") return truncateBodyText(item.bodyText);
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "boolean") return value ? "Yes" : "No";
          if (typeof value === "object") return JSON.stringify(value);
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceTopicsTable() {
  const [items, setItems] = useState<IntelligenceTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.topics.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load topics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceTopic>[] = useMemo(
    () => [
      {
        header: "Topic ID",
        accessor: "intelligenceTopicId" as keyof IntelligenceTopic,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Topic Name",
        accessor: "topicName" as keyof IntelligenceTopic,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Slug",
        accessor: "topicSlug" as keyof IntelligenceTopic,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "AD Topic ID",
        accessor: "adTopicId" as keyof IntelligenceTopic,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load topics"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading topics..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="intelligenceTopicId"
              emptyMessage="No topics found."
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="topics"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Module-level cache for the full (unpaginated) Intelligence regions list
// used solely to build the "Parent Region" lookup in IntelligenceRegionsTable
// below. Regions are small, slow-changing reference data, so this is fetched
// once per SPA lifetime rather than on every mount of the table (e.g. every
// time a staff user tabs away from Regions and back). Mirrors the
// fetch-once/cache shape of hooks/useRegions.ts (cachedPromise/cachedValue +
// fetchOnce), but is kept colocated here rather than extracted into its own
// hook: this specific fetch (`intelligence.regions.getAll`, the Intelligence
// `dim_intelligence_region` endpoint) has exactly one consumer today, unlike
// e.g. useCompanyNames.ts which is shared across several pages.
let cachedIntelligenceRegionsPromise: Promise<IntelligenceRegion[]> | null = null;
let cachedIntelligenceRegionsValue: IntelligenceRegion[] | null = null;

function fetchIntelligenceRegionsOnce(): Promise<IntelligenceRegion[]> {
  if (cachedIntelligenceRegionsValue) return Promise.resolve(cachedIntelligenceRegionsValue);
  if (cachedIntelligenceRegionsPromise) return cachedIntelligenceRegionsPromise;
  cachedIntelligenceRegionsPromise = intelligence.regions
    .getAll({ limit: 1000, offset: 0 })
    .then(response => {
      cachedIntelligenceRegionsValue = response.data;
      return response.data;
    })
    .catch(err => {
      cachedIntelligenceRegionsPromise = null;
      throw err;
    });
  return cachedIntelligenceRegionsPromise;
}

function buildRegionNameById(regions: IntelligenceRegion[]): Map<string, string> {
  return new Map(regions.map(region => [region.intelligenceRegionId, region.regionName]));
}

function IntelligenceRegionsTable() {
  const [items, setItems] = useState<IntelligenceRegion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  // Separate, unpaginated lookup of ALL regions so the "Parent Region" column
  // can resolve a parent's name even when the parent lives on a different
  // page than its child in the paginated table above. The regions endpoint
  // caps `limit` at 1000 (see IntelligenceRegionQuerySchema in
  // services/data-marts-api/src/schemas/intelligence.ts), which comfortably
  // covers the full regions reference table in one request. This fetch is
  // intentionally independent of currentPage/itemsPerPage/fetchData, is
  // fetched once per SPA lifetime via the module-level cache above (not once
  // per mount), and failures here are isolated from the main table's `error`
  // state so the paginated table keeps working even if this lookup fails.
  const [regionNameById, setRegionNameById] = useState<Map<string, string>>(() =>
    cachedIntelligenceRegionsValue
      ? buildRegionNameById(cachedIntelligenceRegionsValue)
      : new Map(),
  );

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.regions.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load regions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  useEffect(() => {
    let cancelled = false;

    if (cachedIntelligenceRegionsValue) {
      setRegionNameById(buildRegionNameById(cachedIntelligenceRegionsValue));
      return;
    }

    fetchIntelligenceRegionsOnce()
      .then(allRegions => {
        if (cancelled) return;
        setRegionNameById(buildRegionNameById(allRegions));
      })
      .catch(err => {
        // Swallow lookup failures: the Parent Region column falls back to "-"
        // via regionNameById.get(...) returning undefined. The main
        // paginated table must keep rendering/paginating regardless.
        console.error("Failed to load regions for parent-region lookup:", err);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const columns: Column<IntelligenceRegion>[] = useMemo(
    () => [
      {
        header: "Region ID",
        accessor: "intelligenceRegionId" as keyof IntelligenceRegion,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Parent Region",
        accessor: "parentRegionId" as keyof IntelligenceRegion,
        render: (value: unknown) => {
          if (value === null || value === undefined) return "-";
          const parentName = regionNameById.get(String(value));
          return parentName ?? "-";
        },
      },
      {
        header: "Region Name",
        accessor: "regionName" as keyof IntelligenceRegion,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Slug",
        accessor: "regionSlug" as keyof IntelligenceRegion,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "WP Slug",
        accessor: "wpRegionSlug" as keyof IntelligenceRegion,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Sort Order",
        accessor: "sortOrder" as keyof IntelligenceRegion,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [regionNameById],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load regions"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading regions..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="intelligenceRegionId"
              emptyMessage="No regions found."
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="regions"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IntelligenceCountriesTable() {
  const [items, setItems] = useState<IntelligenceCountry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.countries.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load countries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceCountry>[] = useMemo(
    () => [
      {
        header: "ISO Alpha-2",
        accessor: "countryIsoAlpha2" as keyof IntelligenceCountry,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Region ID",
        accessor: "intelligenceRegionId" as keyof IntelligenceCountry,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Region Name",
        accessor: "regionName" as keyof IntelligenceCountry,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Region Slug",
        accessor: "regionSlug" as keyof IntelligenceCountry,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load countries"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading countries..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="countryIsoAlpha2"
              emptyMessage="No countries found."
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="countries"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IntelligenceEventsTable() {
  const [items, setItems] = useState<IntelligenceEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceEvent | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.events.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceEvent>[] = useMemo(
    () => [
      {
        header: "Title",
        accessor: "eventTitle" as keyof IntelligenceEvent,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Type",
        accessor: "eventTypeName" as keyof IntelligenceEvent,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Topic",
        accessor: "topicName" as keyof IntelligenceEvent,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Country",
        accessor: "countryName" as keyof IntelligenceEvent,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Date",
        accessor: "eventDt" as keyof IntelligenceEvent,
        render: (value: unknown) => (value ? new Date(value as string).toLocaleDateString() : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load events"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading events..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No events found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="events"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.eventTitle || "Event Details"}
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "eventId", label: "Event ID" },
          { key: "intelligenceTopicId", label: "Topic ID" },
          { key: "intelligenceRegionId", label: "Region ID" },
          { key: "intelligenceCountryId", label: "Country ID" },
          { key: "eventTitle", label: "Title" },
          { key: "eventTypeName", label: "Type" },
          { key: "eventUrl", label: "URL" },
          { key: "eventDt", label: "Date" },
          { key: "eventStartDt", label: "Start Date" },
          { key: "eventEndDt", label: "End Date" },
          { key: "countryName", label: "Country" },
          { key: "topicName", label: "Topic" },
          { key: "companyId", label: "Company ID" },
          { key: "companyName", label: "Company" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceEvent, key: keyof IntelligenceEvent) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceGuidesTable() {
  const [items, setItems] = useState<IntelligenceGuide[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.guides.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load guides");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceGuide>[] = useMemo(
    () => [
      {
        header: "Title",
        accessor: "guideTitle" as keyof IntelligenceGuide,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Slug",
        accessor: "guideSlug" as keyof IntelligenceGuide,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Topic ID",
        accessor: "intelligenceTopicId" as keyof IntelligenceGuide,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load guides"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading guides..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No guides found."
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="guides"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function IntelligenceArticleAuthorsTable() {
  const [items, setItems] = useState<IntelligenceArticleAuthor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleAuthor | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleAuthors.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article authors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceArticleAuthor>[] = useMemo(
    () => [
      {
        header: "Article ID",
        accessor: "articleId" as keyof IntelligenceArticleAuthor,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Forename",
        accessor: "authorForename" as keyof IntelligenceArticleAuthor,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Surname",
        accessor: "authorSurname" as keyof IntelligenceArticleAuthor,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Primary Author",
        accessor: "isPrimaryAuthor" as keyof IntelligenceArticleAuthor,
        render: (value: unknown) =>
          value === null || value === undefined ? "-" : value ? "Yes" : "No",
      },
      {
        header: "Sort Order",
        accessor: "sortOrder" as keyof IntelligenceArticleAuthor,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article authors"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading article authors..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No article authors found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="authors"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={
          selected
            ? [selected.authorForename, selected.authorSurname].filter(Boolean).join(" ") ||
              "Author Details"
            : "Author Details"
        }
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "articleId", label: "Article ID" },
          { key: "contactId", label: "Contact ID" },
          { key: "authorTitle", label: "Title" },
          { key: "authorForename", label: "Forename" },
          { key: "authorSurname", label: "Surname" },
          { key: "authorMiddleName", label: "Middle Name" },
          { key: "authorExternalProfileUrl", label: "External Profile URL" },
          { key: "authorProfileSlug", label: "Profile Slug" },
          { key: "authorImageUrl", label: "Image URL" },
          { key: "isPrimaryAuthor", label: "Primary Author" },
          { key: "sortOrder", label: "Sort Order" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceArticleAuthor, key: keyof IntelligenceArticleAuthor) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "boolean") return value ? "Yes" : "No";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceArticleTopicsTable() {
  const [items, setItems] = useState<IntelligenceArticleTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleTopic | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleTopics.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article topics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceArticleTopic>[] = useMemo(
    () => [
      {
        header: "Article ID",
        accessor: "articleId" as keyof IntelligenceArticleTopic,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Topic Name",
        accessor: "topicName" as keyof IntelligenceArticleTopic,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Topic Slug",
        accessor: "topicSlug" as keyof IntelligenceArticleTopic,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Primary Topic",
        accessor: "isPrimaryTopic" as keyof IntelligenceArticleTopic,
        render: (value: unknown) =>
          value === null || value === undefined ? "-" : value ? "Yes" : "No",
      },
      {
        header: "Is Parent",
        accessor: "isParent" as keyof IntelligenceArticleTopic,
        render: (value: unknown) =>
          value === null || value === undefined ? "-" : value ? "Yes" : "No",
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article topics"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading article topics..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No article topics found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="topics"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.topicName || "Article Topic Details"}
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "articleId", label: "Article ID" },
          { key: "intelligenceTopicId", label: "Topic ID" },
          { key: "intelligenceParentTopicId", label: "Parent Topic ID" },
          { key: "isParent", label: "Is Parent" },
          { key: "isPrimaryTopic", label: "Primary Topic" },
          { key: "topicName", label: "Topic Name" },
          { key: "topicSlug", label: "Topic Slug" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceArticleTopic, key: keyof IntelligenceArticleTopic) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "boolean") return value ? "Yes" : "No";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceArticleDisclaimersTable() {
  const [items, setItems] = useState<IntelligenceArticleDisclaimer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleDisclaimer | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleDisclaimers.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article disclaimers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceArticleDisclaimer>[] = useMemo(
    () => [
      {
        header: "Article ID",
        accessor: "articleId" as keyof IntelligenceArticleDisclaimer,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Company Rules ID",
        accessor: "companyRulesId" as keyof IntelligenceArticleDisclaimer,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Disclaimer Text",
        accessor: "disclaimerText" as keyof IntelligenceArticleDisclaimer,
        render: (value: unknown) => {
          const text = value as string | null;
          if (!text) return "-";
          return text.length > 80 ? `${text.slice(0, 80)}...` : text;
        },
      },
      {
        header: "Sort Order",
        accessor: "sortOrder" as keyof IntelligenceArticleDisclaimer,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article disclaimers"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading article disclaimers..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No article disclaimers found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="disclaimers"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={`Disclaimer — Article ${selected?.articleId ?? ""}`}
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "articleId", label: "Article ID" },
          { key: "companyRulesId", label: "Company Rules ID" },
          { key: "disclaimerText", label: "Disclaimer Text" },
          { key: "sortOrder", label: "Sort Order" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(
          item: IntelligenceArticleDisclaimer,
          key: keyof IntelligenceArticleDisclaimer,
        ) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceArticleStatesTable() {
  const [items, setItems] = useState<IntelligenceArticleState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceArticleState | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.articleStates.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load article states");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceArticleState>[] = useMemo(
    () => [
      {
        header: "Article ID",
        accessor: "articleId" as keyof IntelligenceArticleState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "State Name",
        accessor: "stateName" as keyof IntelligenceArticleState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "State Code",
        accessor: "stateCode" as keyof IntelligenceArticleState,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "Country ID",
        accessor: "countryId" as keyof IntelligenceArticleState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load article states"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading article states..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="tablePk"
              emptyMessage="No article states found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="states"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.stateName || "Article State Details"}
        fields={[
          { key: "tablePk", label: "Table PK" },
          { key: "articleId", label: "Article ID" },
          { key: "stateId", label: "State ID" },
          { key: "countryId", label: "Country ID" },
          { key: "stateName", label: "State Name" },
          { key: "stateCode", label: "State Code" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceArticleState, key: keyof IntelligenceArticleState) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          return String(value);
        }}
      />
    </div>
  );
}

function IntelligenceStatesTable() {
  const [items, setItems] = useState<IntelligenceState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<IntelligenceState | null>(null);

  const fetchData = useCallback(async (page: number, pageSize: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await intelligence.states.getAll({
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setItems(response.data);
      setTotal(response.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load states");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage);
  }, [fetchData, currentPage, itemsPerPage]);

  const columns: Column<IntelligenceState>[] = useMemo(
    () => [
      {
        header: "State Name",
        accessor: "stateName" as keyof IntelligenceState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "State Code",
        accessor: "stateCode" as keyof IntelligenceState,
        render: (value: unknown) => (value as string | null) || "-",
      },
      {
        header: "State ID",
        accessor: "stateId" as keyof IntelligenceState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
      {
        header: "Country ID",
        accessor: "countryId" as keyof IntelligenceState,
        render: (value: unknown) => (value !== null && value !== undefined ? String(value) : "-"),
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load states"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      {loading ? (
        <LoadingState message="Loading states..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={items}
              columns={columns}
              rowKey="stateId"
              emptyMessage="No states found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="states"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.stateName || "State Details"}
        fields={[
          { key: "stateId", label: "State ID" },
          { key: "countryId", label: "Country ID" },
          { key: "stateName", label: "State Name" },
          { key: "stateCode", label: "State Code" },
          { key: "rowSyncedTs", label: "Row Synced" },
        ]}
        renderValue={(item: IntelligenceState, key: keyof IntelligenceState) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          return String(value);
        }}
      />
    </div>
  );
}

type CompanySummaryRow = CompanySummaryListItem & { _rowId: string };

function CompanySummariesTable() {
  const [data, setData] = useState<CompanySummaryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<CompanySummaryListItem | null>(null);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async (page: number, pageSize: number, searchTerm: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await companySummaries.getAll({
        search: searchTerm || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      setData(
        response.summaries.map(s => ({
          ...s,
          _rowId: `${s.companySlug}:${s.practiceAreaSlug}:${s.countrySlug}:${s.site}`,
        })),
      );
      setTotal(response.pagination.total);
    } catch (err) {
      console.error("[CompanySummariesTable] Failed to load:", err);
      setError(err instanceof Error ? err.message : "Failed to load company summaries");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(currentPage, itemsPerPage, search);
  }, [fetchData, currentPage, itemsPerPage, search]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const columns: Column<CompanySummaryRow>[] = useMemo(
    () => [
      { header: "Company", accessor: "companyName" as keyof CompanySummaryRow },
      { header: "Practice Area", accessor: "practiceAreaSlug" as keyof CompanySummaryRow },
      { header: "Country", accessor: "countrySlug" as keyof CompanySummaryRow },
      { header: "Site", accessor: "site" as keyof CompanySummaryRow },
      {
        header: "Offices",
        accessor: "globalOfficeCount" as keyof CompanySummaryRow,
        render: (value: unknown) => String(value ?? "-"),
      },
      {
        header: "Synced",
        accessor: "syncedAt" as keyof CompanySummaryRow,
        render: (value: unknown) => {
          const date = value as string | null;
          return date ? new Date(date).toLocaleString() : "-";
        },
      },
    ],
    [],
  );

  const totalPages = Math.ceil(total / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, total);

  if (error) {
    return (
      <EmptyState
        variant="error"
        title="Failed to load company summaries"
        message={error}
        action={{ label: "Retry", onClick: () => fetchData(currentPage, itemsPerPage, search) }}
      />
    );
  }

  return (
    <div className={styles.tableDataContainer}>
      <div className={styles.tableControls}>
        <div className={styles.searchContainer}>
          <SearchBar
            value={search}
            onChange={handleSearch}
            onClear={() => handleSearch("")}
            placeholder="Search by company name, practice area, country..."
          />
        </div>
      </div>

      {loading ? (
        <LoadingState message="Loading company summaries..." />
      ) : (
        <>
          <div className={styles.dataTableContainer}>
            <DataTable
              data={data}
              columns={columns}
              rowKey="_rowId"
              emptyMessage="No company summaries found."
              onRowClick={setSelected}
            />
          </div>

          {total > 0 && (
            <div className={styles.paginationContainer}>
              <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                startIndex={startIndex}
                endIndex={endIndex}
                totalCount={total}
                itemName="summaries"
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}

      <DetailModal
        isOpen={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        title={selected?.companyName || "Company Summary"}
        fields={[
          { key: "companyId", label: "Company ID" },
          { key: "companyName", label: "Company Name" },
          { key: "companySlug", label: "Company Slug" },
          { key: "practiceAreaSlug", label: "Practice Area Slug" },
          { key: "countrySlug", label: "Country Slug" },
          { key: "site", label: "Site" },
          { key: "logoUrl", label: "Logo URL" },
          { key: "practiceArea", label: "Practice Area" },
          { key: "practiceGroup", label: "Practice Group" },
          { key: "globalOfficeCount", label: "Global Offices" },
          { key: "localOfficeCount", label: "Local Offices" },
          { key: "jurisdictionCount", label: "Jurisdictions" },
          { key: "rankingTables", label: "Ranking Tables" },
          { key: "ratings", label: "Ratings" },
          { key: "activeClients", label: "Active Clients" },
          { key: "workHighlights", label: "Work Highlights" },
          { key: "testimonials", label: "Testimonials" },
          { key: "comparativeGuides", label: "Comparative Guides" },
          { key: "financials", label: "Financials" },
          { key: "siteStructureIds", label: "Site Structure IDs" },
          { key: "syncedAt", label: "Synced At" },
        ]}
        renderValue={(item: CompanySummaryListItem, key: keyof CompanySummaryListItem) => {
          const value = item[key];
          if (value === null || value === undefined) return "N/A";
          if (typeof value === "object") {
            return (
              <pre
                style={{
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "12px",
                  maxHeight: "300px",
                  overflow: "auto",
                }}
              >
                {JSON.stringify(value, null, 2)}
              </pre>
            );
          }
          return String(value);
        }}
      />
    </div>
  );
}

function DataMartDetail() {
  const { dataMartId } = useParams<{ dataMartId: string }>();
  const navigate = useNavigate();
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [sidebarWidth, setSidebarWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const dataMart = dataMartId ? DATA_MARTS[dataMartId] : null;

  useEffect(() => {
    if (dataMart?.tables.length === 1) {
      setSelectedTable(dataMart.tables[0].id);
    }
  }, [dataMart]);

  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;

      const minWidth = 280;
      const maxWidth = containerRect.width * 0.5;

      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  if (!dataMart) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <h1 className={styles.title}>Data Mart Not Found</h1>
        </div>
        <EmptyState
          variant="error"
          title="Data mart not found"
          message="The requested data mart does not exist."
          action={{
            label: "Back to Data Marts",
            onClick: () => navigate("/data-marts"),
          }}
        />
      </div>
    );
  }

  const renderTableData = () => {
    if (!selectedTable) {
      return (
        <div className={styles.emptyState}>
          <Icon icon="table" size="xlarge" className={styles.emptyStateIcon} />
          <p className={styles.emptyStateText}>Select a table to view its data</p>
        </div>
      );
    }

    if (dataMartId === "account" && selectedTable === "company-users") {
      return <CompanyUsersTable />;
    }

    if (dataMartId === "rankings" && selectedTable === "fct-rankings") {
      return <RankingsTable />;
    }

    if (
      dataMartId === "network-jurisdiction-rankings" &&
      selectedTable === "network-jurisdiction-ranking"
    ) {
      return <NetworkJurisdictionRankingsTable />;
    }

    if (dataMartId === "company-summaries" && selectedTable === "company-summary") {
      return <CompanySummariesTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-feed") {
      return <IntelligenceArticleFeedTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-detail") {
      return <IntelligenceArticleDetailTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "topics") {
      return <IntelligenceTopicsTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "regions") {
      return <IntelligenceRegionsTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "countries") {
      return <IntelligenceCountriesTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "events") {
      return <IntelligenceEventsTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "guides") {
      return <IntelligenceGuidesTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-authors") {
      return <IntelligenceArticleAuthorsTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-topics") {
      return <IntelligenceArticleTopicsTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-disclaimers") {
      return <IntelligenceArticleDisclaimersTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "article-states") {
      return <IntelligenceArticleStatesTable />;
    }

    if (dataMartId === "intelligence" && selectedTable === "states") {
      return <IntelligenceStatesTable />;
    }

    return (
      <div className={styles.emptyState}>
        <Icon icon="table" size="xlarge" className={styles.emptyStateIcon} />
        <p className={styles.emptyStateText}>Table data not available</p>
      </div>
    );
  };

  const selectedTableConfig = dataMart.tables.find(t => t.id === selectedTable);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{dataMart.name}</h1>
      </div>

      <div className={styles.mainContent} ref={containerRef}>
        <div className={styles.sidebar} style={{ width: `${sidebarWidth}px` }}>
          <div className={styles.tableListContainer}>
            <div className={styles.tableListHeader}>
              <span className={styles.tableListTitle}>Tables</span>
              <span className={styles.tableCount}>{dataMart.tables.length}</span>
            </div>
            <div className={styles.tableList}>
              {dataMart.tables.map(table => (
                <button
                  key={table.id}
                  className={`${styles.tableItem} ${selectedTable === table.id ? styles.tableItemSelected : ""}`}
                  onClick={() => setSelectedTable(table.id)}
                >
                  <div className={styles.tableItemIcon}>
                    <Icon icon="table" size="small" />
                  </div>
                  <div className={styles.tableItemContent}>
                    <span className={styles.tableItemName}>{table.name}</span>
                    <span className={styles.tableItemDescription}>{table.description}</span>
                  </div>
                  <Icon icon="chevron-right" size="small" className={styles.tableItemArrow} />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`${styles.resizer} ${isResizing ? styles.resizerActive : ""}`}
          onMouseDown={handleMouseDown}
        >
          <div className={styles.resizerHandle}>
            <Icon icon="grip-vertical" size="small" />
          </div>
        </div>

        <div className={styles.contentPanel}>
          {selectedTable && selectedTableConfig && (
            <div className={styles.contentHeader}>
              <div className={styles.contentHeaderInfo}>
                <h2 className={styles.contentTitle}>{selectedTableConfig.name}</h2>
                <p className={styles.contentDescription}>{selectedTableConfig.description}</p>
              </div>
            </div>
          )}
          <div className={styles.contentBody}>{renderTableData()}</div>
        </div>
      </div>
    </div>
  );
}

export default DataMartDetail;
